/**
 * What the drop in front of the user actually carries.
 *
 * Five live singletons, on the same pattern (and for the same reason) as `CHANNELS` /
 * `FIELD_SCALE` in `snapshot.ts`: the widgets and the copy need them synchronously while the
 * snapshot arrives asynchronously, so `hydrate()` overwrites them IN PLACE before anything that
 * reads them can paint. Their seeds describe the demo provider, so nothing branches on source.
 *
 * They exist so no caption has to assume what a drop covers:
 *   - `BASIS` — how the two feeds are DATED. Stock on hand and shipped volume are separate
 *     positions on separate dates; every surface that puts "held" beside "shipped" says so.
 *   - `SIM_FEEDS` — which feeds the drop does not measure, so a simulated per-SKU attribute is
 *     never rendered as a site fact.
 *   - `MONTH_SLOTS` — the fiscal months the sales window spans, so the monthly chart is as wide
 *     as the data and no wider.
 *   - `CUR` — the reporting currency, so no surface paints a hardcoded symbol over foreign money.
 *   - `DEPTH` — how `Sku.faces` was derived, in the build's own words, so a footprint is never
 *     quoted as a count of the site's planning faces.
 *
 * No imports: everything else in the data layer may read this, including `demo.ts`.
 */

/** Single-letter month labels — the demo's own 12-month mix. */
export const MONTHS = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"] as const;

const ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** One bar on the monthly NTS chart: which `Sku.mon` slot it reads, and how it is labelled. */
export interface MonthSlot {
  /** Index into `Sku.mon` (0 = January). */
  mon: number;
  label: string;
}

/** Fiscal months the drop covers. Seed = the demo's full simulated year. */
export const MONTH_SLOTS: MonthSlot[] = MONTHS.map((label, mon) => ({ mon, label }));

/**
 * Slots for the `YYYY-MM` months a drop reports, in the order reported.
 *
 * De-duplicated by calendar month: a drop spanning more than twelve months folds two fiscal
 * months onto one slot, which the build already reports as `monthSlotsMerged` — one bar per
 * slot is the only read that does not double-count. Returns `[]` when nothing parses, so the
 * caller can keep the seed and say the months were unreadable rather than draw an empty chart.
 */
export function monthSlots(months: readonly string[]): MonthSlot[] {
  const mons: number[] = [];
  for (const m of months) {
    const mon = Number(m.slice(-2)) - 1;
    if (Number.isInteger(mon) && mon >= 0 && mon <= 11 && !mons.includes(mon)) {
      mons.push(mon);
    }
  }
  // A short drop names the period on the axis; a long one would not fit the year twelve times.
  const withYear = mons.length <= 4;
  return mons.map((mon, i) => ({
    mon,
    label: withYear ? `${ABBR[mon]} ${(months[i] ?? "").slice(0, 4)}`.trim() : (ABBR[mon] ?? ""),
  }));
}

/**
 * How the drop dates its two feeds.
 *
 * `salesSpan` is the window the shipped figures are read over; `inventoryAsOf` is the date of
 * the stock position. They are not the same period — on the current drop the stock is months
 * later — so `gap` carries the distance in plain language for the surfaces that show both.
 */
export interface Basis {
  salesSpan: string;
  /** Stock-position date, or `null` when the drop carries none. */
  inventoryAsOf: string | null;
  /** e.g. `about N months after`, relative to the end of the sales window. */
  gap: string | null;
}

export const BASIS: Basis = { salesSpan: "the review window", inventoryAsOf: null, gap: null };

/** The date fields a drop may carry, under any of the names the build has used for them. */
export interface DropDates {
  monthsCovered?: readonly string[];
  salesWindow?: {
    first?: string;
    last?: string;
    from?: string;
    to?: string;
    start?: string;
    end?: string;
  };
  inventoryAsOf?: string | readonly string[];
  inventorySnapDates?: readonly string[];
  /** Days between the end of the sales window and the stock position, when the build measures it. */
  gapDays?: number;
}

/** Average month in days — enough to say "about 6 months", not a date library. */
const MONTH_DAYS = 30.4375;

/** Days as months, in plain language. `after` is which side of the window the stock sits. */
function gapPhrase(days: number, after: boolean): string {
  const months = Math.round(Math.abs(days) / MONTH_DAYS);
  return months < 1
    ? "within a month of"
    : `about ${months} month${months > 1 ? "s" : ""} ${after ? "after" : "before"}`;
}

/**
 * How far the stock position sits from the end of the sales window, in plain language.
 * Null when either date is unreadable, so an unknown gap is never described as no gap.
 */
function gapWording(salesEnd: string, asOf: string): string | null {
  // A `YYYY-MM` sales end means that month's end, which is what the stock date is measured from.
  const end = new Date(salesEnd.length === 7 ? `${salesEnd}-28` : salesEnd).getTime();
  const stock = new Date(asOf).getTime();
  if (Number.isNaN(end) || Number.isNaN(stock)) {
    return null;
  }
  return gapPhrase((stock - end) / 864e5, stock >= end);
}

/**
 * Derive the dated basis from whatever dates the drop carries.
 *
 * ponytail: reads every spelling the build has used for each date (`first`/`last`, `from`/`to`,
 * `start`/`end`; `inventoryAsOf` or `inventorySnapDates`). Collapse to one when the contract
 * settles — the cost of guessing wrong is a caption that silently stops saying anything.
 */
export function describeBasis(d: DropDates): Basis {
  const months = d.monthsCovered ?? [];
  const monthSpan =
    months.length > 1 ? `${months[0]} to ${months[months.length - 1]}` : (months[0] ?? "unknown");
  const w = d.salesWindow;
  const from = w?.first ?? w?.from ?? w?.start;
  const to = w?.last ?? w?.to ?? w?.end;
  const dates = [
    ...(typeof d.inventoryAsOf === "string" ? [d.inventoryAsOf] : (d.inventoryAsOf ?? [])),
    ...(d.inventorySnapDates ?? []),
  ].filter((s) => s);
  // The newest export is the position the payload plots — the build supersedes the older ones.
  const asOf = dates.length ? [...dates].sort().pop() : null;
  // A measured gap beats one estimated off two date strings, when the build ships it.
  const gap =
    d.gapDays !== undefined ? gapPhrase(d.gapDays, d.gapDays >= 0) : gapWording(to ?? monthSpan, asOf ?? ""); // prettier-ignore
  return {
    salesSpan: from && to ? `${from} to ${to}` : monthSpan,
    inventoryAsOf: asOf ?? null,
    gap: asOf ? gap : null,
  };
}

/**
 * The annualisation, in one sentence, or `""` on a drop that observed a full year.
 *
 * Every money figure on the screen is the observed window scaled to twelve months. The rail
 * caption and the lens captions both have to say so — a disclosure that lives on one branch of one
 * widget disappears the moment anything is selected — so both compose it here, from the drop's own
 * month count and its own dates. Twelve months returns nothing rather than claiming a scaling that
 * did not happen.
 */
export function annualisedNote(months: number): string {
  if (!(months > 0) || months >= 12) {
    return "";
  }
  const span = months === 1 ? "single fiscal month" : `${months} fiscal months`;
  return `Money is annualised: net trade sales over the ${span} this drop covers (${BASIS.salesSpan}) × 12/${months}, not a measured year.`;
}

/**
 * Reporting currency of every served money figure, as an ISO code.
 *
 * A fourth live singleton for the same reason as the three above: the plot paints its y-axis
 * title and hover card from typed arrays, with no `Site` in scope, and it painted a hardcoded
 * `£` over euro values. `curSym` turns the code into a symbol (or leaves the code, which is
 * wrong-looking rather than wrong). Seed = the demo anchor.
 */
export const CUR = { nts: "GBP" };

/**
 * How a SKU's `faces` figure is arrived at, in the build's own words.
 *
 * A fifth live singleton, and the one that closes the pick-face problem properly. `faces` is boxes
 * held divided by a depth scale the build calibrates from the drop's own inventory distribution —
 * so Σfaces is a FOOTPRINT and is multiples of the site's planning capacity, which is why quoting
 * it as "pick faces" put a five-figure count beside a four-figure ceiling.
 *
 * The build serves the sentence (`calibration.pickFace.rule`) precisely so no screen has to
 * reconstruct it and drift from the divisor. Seed = the wording that applies when nothing is
 * served, which is also the demo's case.
 */
export const DEPTH = {
  rule: "face-equivalents from boxes held on a nominal face depth — a footprint, not counted pick faces",
};

/**
 * Feeds this drop does not measure, by the build's own feed names.
 *
 * Seed: the launch date is the one per-SKU attribute still surfaced as a fact about a named SKU
 * without a feed behind it. Pick location left this list when the current slot did: the slotting
 * surfaces now show a recommendation derived from measured velocity and depth, and nothing claims
 * where a SKU sits today.
 */
export const SIM_FEEDS: string[] = ["launch-date"];

/** True when `kind` names a feed the drop did not carry — drives the `(est.)` markers. */
export function simulated(kind: string): boolean {
  return SIM_FEEDS.some((f) => f.includes(kind));
}
